import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { SpaceGroup } from '@signal-os/lock-space-meili-docs-types';
import { ReactNode } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { SetSortController } from 'src/hooks/use-sort-controller';
import { SortOrder } from 'src/types/enum/sort-order';
import { SortController } from 'src/types/interfaces/sort-controller';
import { TableColumn } from 'src/types/interfaces/table-column';
import { handleUpdateSort } from 'src/utils/sort/handle-update-sort';
import {
  EmployeesGroupDetailsSpaceGroupsTableRow,
  SpaceGroupsActionsSlotPayload,
} from './space-groups-table-row';

interface Props {
  data: SpaceGroup[];
  isLoading: boolean;
  error: string | undefined;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
  actionsSlot: (payload: SpaceGroupsActionsSlotPayload) => ReactNode;
}

export const EmployeesGroupDetailsSpaceGroupsTable = ({
  isLoading,
  data,
  error,
  sortController,
  setSortController,
  handleChangeSort,
  actionsSlot,
}: Props) => {
  const { t } = useTranslation('employeesGroups');

  const columns: TableColumn[] = [
    {
      id: 'email',
      label: t('spaceGroups.columns.name'),
      isSortable: true,
    },
    {
      id: 'otherEmployeeGroups',
      label: t('spaceGroups.columns.otherEmployeeGroups'),
      isSortable: false,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={sortController.sortBy === column.id}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() =>
                        handleUpdateSort(
                          setSortController,
                          handleChangeSort,
                          column.id,
                        )
                      }
                      key={column.id}>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}

              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(({ token, name, employeeGroupsTokens }) => {
              return (
                <EmployeesGroupDetailsSpaceGroupsTableRow
                  key={token}
                  token={token}
                  name={name}
                  employeeGroupsTokens={employeeGroupsTokens}
                  actionsSlot={actionsSlot}
                />
              );
            })}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
};
